import { useState, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLogger } from '@archipro-website/logger/client';

interface AIImageData {
    imageUpscale: {
        url: string;
        width: number;
        height: number;
        fileKey: string;
        __typename: string;
        Message?: string;
    };
}

interface UseAIUpscaleApiOptions {
    onCompleted?: (data: AIImageData) => void;
    onError?: (error: string) => void;
}

interface UpscaleApiResponse {
    success: boolean;
    data?: AIImageData;
    error?: string;
}

/**
 * Custom hook for AI image upscaling using Remix API route
 */
export const useAIUpscaleApi = (libraryImageID: number) => {
    const fetcher = useFetcher<UpscaleApiResponse>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const logger = useLogger();

    // Store options in a ref to avoid stale closures in useEffect
    const optionsRef = useRef<UseAIUpscaleApiOptions>();

    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data) {
            if (fetcher.data.success) {
                if (fetcher.data.data) {
                    optionsRef.current?.onCompleted?.(fetcher.data.data);
                }
            } else {
                const fullErrorMessage =
                    fetcher.data.error || 'Unknown error occurred';
                const userFriendlyMessage =
                    'Failed to upscale image. Please try again or post in #support.';

                // Log the full error for debugging
                logger.warning('AI Upscale Error', {
                    error: fullErrorMessage,
                    libraryImageID,
                    fetcherState: fetcher.state,
                });

                setError(userFriendlyMessage);
                optionsRef.current?.onError?.(userFriendlyMessage);
            }
            setIsLoading(false);
        } else if (fetcher.state === 'idle' && fetcher.data === undefined) {
            // Reset loading and error when fetcher goes idle without data (e.g., after initial load or a form submission that doesn't return data immediately)
            setIsLoading(false);
            setError(null);
        }
    }, [fetcher.state, fetcher.data, libraryImageID, logger]);

    const triggerUpscale = async (options?: UseAIUpscaleApiOptions) => {
        setIsLoading(true);
        setError(null);
        optionsRef.current = options; // Update the ref with current options

        const formData = new FormData();
        formData.append('libraryImageID', libraryImageID.toString());

        fetcher.submit(formData, {
            method: 'POST',
            action: '/remix-api/admin/upscale-image',
        });

        // The response handling will now be done in the useEffect hook
    };

    return [
        triggerUpscale,
        {
            loading: isLoading || fetcher.state === 'submitting',
            error:
                error ||
                (fetcher.data?.success === false ? fetcher.data.error : null),
            data: fetcher.data?.success ? fetcher.data.data : null,
        },
    ] as const;
};
